import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Upload, Scissors, Palette, Truck, ArrowRight, Check, Tag, Facebook, Instagram, Youtube } from "lucide-react";
import Navbar from "../components/Navbar";
import FaqSection from "../components/FaqSection";
import Seo from "../components/Seo";
import { finishByKey } from "../lib/finishes";
import { useI18n, formatCAD } from "../i18n";
import { api, LOGO_URL } from "../lib/api";

export default function Landing() {
  const { t } = useI18n();
  const [bulk, setBulk] = useState(null);
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.get("/price/bulk-table").then(({ data }) => setBulk(data)).catch(() => {});
    api.get("/content").then(({ data }) => setContent(data)).catch(() => {});
  }, []);

  const steps = [
    { icon: Upload, title: t("landing.s1t"), text: t("landing.s1x") },
    { icon: Scissors, title: t("landing.s2t"), text: t("landing.s2x") },
    { icon: Palette, title: t("landing.s3t"), text: t("landing.s3x") },
    { icon: Truck, title: t("landing.s4t"), text: t("landing.s4x") },
  ];
  const PRODUCTS = [
    { id: "white-vinyl-stickers", mat: "white_vinyl", name: "White Vinyl Stickers", color: "bg-white", desc: t("landing.pWhite"),
      img: "https://images.unsplash.com/photo-1749434481366-aa472f806a81?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
      alt: "Custom white vinyl sticker with full-colour logo printing" },
    { id: "transparent-stickers", mat: "transparent_vinyl", name: "Transparent Stickers", color: "checkerboard", desc: t("landing.pClear"),
      img: "https://images.unsplash.com/photo-1621252756235-7f37e5e5125e?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
      alt: "Custom transparent stickers applied to clear product packaging" },
    { id: "chrome-stickers", mat: "chrome_vinyl", name: "Chrome Stickers", color: "chrome-swatch", desc: t("landing.pChrome"),
      img: "https://images.unsplash.com/photo-1668510074638-6a4ad07c1021?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
      alt: "Custom chrome sticker with reflective metallic silver finish" },
    { id: "holographic-stickers", mat: "holographic_vinyl", name: "Holographic Stickers", color: "bg-gradient-to-br from-nbcyan via-nbpink to-nbyellow", desc: t("landing.pHolo"),
      img: "https://images.unsplash.com/photo-1699004642536-b790e3b41f9c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
      alt: "Custom holographic sticker showing a rainbow colour-shifting effect" },
  ];

  return (
    <div className="App">
      <Seo
        path="/"
        title={t("landing.seoTitle")}
        description={t("landing.seoDesc")}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 dot-grid pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center relative">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 bg-nbyellow nb-border nb-shadow-sm rounded-full px-4 py-1.5 font-head font-bold text-sm mb-6"
            >
              <span className="w-2.5 h-2.5 rounded-full bg-nbpink animate-pulse" /> {t("landing.badge")}
            </motion.div>
            <h1 className="font-head font-extrabold text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mb-6">
              {t("landing.heroPre")}<span className="rainbow-text">{t("landing.heroAccent")}</span>.
            </h1>
            <p className="text-lg sm:text-xl text-nbblack/70 max-w-xl mb-8">{t("landing.heroSub")}</p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/design"
                data-testid="hero-cta"
                className="inline-flex items-center gap-2 bg-nbpink text-white font-head font-bold text-lg px-7 py-3.5 rounded-full nb-border nb-shadow nb-press"
              >
                {t("landing.start")} <ArrowRight size={20} />
              </Link>
              <a href="#how" className="inline-flex items-center gap-2 bg-white font-head font-bold text-lg px-7 py-3.5 rounded-full nb-border nb-shadow nb-press">
                {t("landing.how")}
              </a>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-8 text-sm font-semibold text-nbblack/70">
              {[t("landing.f1"), t("landing.f2"), t("landing.f3")].map((f) => (
                <span key={f} className="inline-flex items-center gap-1.5">
                  <Check size={16} className="text-nbpink" /> {f}
                </span>
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9, rotate: -3 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative flex items-center justify-center py-6"
          >
            <motion.img
              src="/hero-sticker.png"
              alt="2T Sticker die-cut sticker"
              className="w-full max-w-xl h-auto animate-float"
              style={{ filter: "drop-shadow(10px 14px 10px rgba(0,0,0,0.28))", transform: "rotate(-4deg)" }}
              whileHover={{ rotate: 0, scale: 1.03 }}
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
              data-testid="hero-sticker"
            />
            <div className="absolute -bottom-2 right-2 sm:right-8 bg-nbcyan nb-border nb-shadow rounded-2xl px-5 py-3 font-head font-extrabold text-xl rotate-6">
              {t("landing.fromPrice")}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-nbblack text-white py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <h2 className="font-head font-extrabold text-4xl sm:text-5xl mb-3">{t("landing.howTitle")}</h2>
          <p className="text-white/60 text-lg mb-12 max-w-2xl">{t("landing.howSub")}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <div key={i} className="bg-white text-nbblack rounded-2xl p-6 nb-border nb-shadow relative">
                <div className="absolute -top-4 -right-4 w-10 h-10 rounded-full bg-nbyellow nb-border flex items-center justify-center font-head font-extrabold">
                  {i + 1}
                </div>
                <s.icon size={34} className="mb-4 text-nbpink" strokeWidth={2.4} />
                <h3 className="font-head font-bold text-xl mb-1">{s.title}</h3>
                <p className="text-nbblack/60">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bulk savings */}
      {bulk?.rows?.length > 0 && (
        <section className="pt-20 pb-6">
          <div className="max-w-4xl mx-auto px-5 sm:px-8">
            <div className="bg-nbyellow nb-border nb-shadow rounded-3xl p-8 sm:p-10">
              <div className="flex items-center gap-3 mb-2">
                <Tag size={30} strokeWidth={2.4} />
                <h2 className="font-head font-extrabold text-3xl sm:text-4xl">{t("landing.bulkTitle")}</h2>
              </div>
              <p className="text-nbblack/70 text-base mb-2 max-w-xl">{t("landing.bulkSub")}</p>
              <p className="text-nbblack/50 text-sm mb-6">{t("landing.bulkSample")} {bulk.sample?.label}.</p>

              <div className="bg-white nb-border rounded-2xl overflow-hidden" data-testid="bulk-savings-table">
                <div className="grid grid-cols-3 gap-2 px-5 py-3 border-b-2 border-nbblack/10 font-head font-extrabold text-xs sm:text-sm text-nbblack/60 uppercase tracking-wide">
                  <span>{t("landing.bulkColQty")}</span>
                  <span className="text-center">{t("landing.bulkColEach")}</span>
                  <span className="text-right">{t("landing.bulkColSave")}</span>
                </div>
                {bulk.rows.map((r) => (
                  <div
                    key={r.qty}
                    data-testid={`bulk-row-${r.qty}`}
                    className={`grid grid-cols-3 gap-2 px-5 py-3 items-center border-b border-nbblack/5 last:border-0 ${r.popular ? "bg-nbcyan/40" : ""}`}
                  >
                    <span className="font-head font-bold flex items-center gap-2">
                      {r.qty.toLocaleString()}
                      {r.popular && (
                        <span className="text-[10px] font-extrabold bg-nbpink text-white px-2 py-0.5 rounded-full nb-border uppercase">{t("landing.bulkPopular")}</span>
                      )}
                    </span>
                    <span className="text-center font-head font-extrabold text-lg">{formatCAD(r.unit_price)}</span>
                    <span className="text-right">
                      {r.savings_pct > 0 ? (
                        <span className="font-head font-extrabold text-nbpink">-{r.savings_pct}%</span>
                      ) : (
                        <span className="text-nbblack/30">—</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <Link to="/design" data-testid="bulk-cta" className="inline-flex items-center gap-2 bg-nbblack text-white font-head font-bold text-lg px-7 py-3.5 rounded-full nb-border nb-shadow nb-press">
                  {t("landing.bulkCta")} <ArrowRight size={20} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Shop by sticker type — semantic product sections (SEO) */}
      <section className="py-20" aria-labelledby="products-heading">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <h2 id="products-heading" className="font-head font-extrabold text-4xl sm:text-5xl mb-3">{t("landing.matTitle")}</h2>
          <p className="text-nbblack/60 text-lg mb-8 max-w-2xl">{t("landing.matSub")}</p>

          <nav aria-label={t("landing.shopByType")} className="flex flex-wrap gap-3 mb-14">
            {PRODUCTS.map((p) => (
              <a
                key={p.id}
                href={`#${p.id}`}
                data-testid={`shop-link-${p.id}`}
                className="inline-flex items-center gap-2 bg-white font-head font-bold text-sm px-4 py-2 rounded-full nb-border nb-shadow-sm nb-press hover:bg-nbyellow transition-colors"
              >
                <span className={`w-4 h-4 rounded-full border-2 border-nbblack ${p.color}`} />
                {p.name}
              </a>
            ))}
          </nav>

          <div className="space-y-16">
            {PRODUCTS.map((p, i) => (
              <section
                key={p.id}
                id={p.id}
                data-testid={`product-section-${p.id}`}
                className="grid md:grid-cols-2 gap-8 items-center scroll-mt-28"
              >
                <div className={`rounded-3xl overflow-hidden nb-border nb-shadow ${i % 2 === 1 ? "md:order-2" : ""}`}>
                  <img
                    src={p.img}
                    alt={p.alt}
                    width="640"
                    height="420"
                    loading="lazy"
                    className="w-full h-64 sm:h-80 object-cover"
                  />
                </div>
                <div>
                  <div className={`inline-block w-14 h-14 rounded-2xl border-[3px] border-nbblack mb-4 ${p.color}`} />
                  <h2 className="font-head font-extrabold text-3xl sm:text-4xl mb-3">{p.name}</h2>
                  <p className="text-nbblack/70 text-base leading-relaxed mb-6">{p.desc}</p>
                  <Link
                    to={`/stickers/${finishByKey(p.mat)?.slug || "white"}`}
                    data-testid={`product-cta-${p.id}`}
                    className="inline-flex items-center gap-2 bg-nbpink text-white font-head font-bold px-6 py-3 rounded-full nb-border nb-shadow nb-press"
                  >
                    {t("landing.pOrderCta")} <ArrowRight size={18} />
                  </Link>
                </div>
              </section>
            ))}
          </div>

          <div className="text-center mt-16">
            <Link
              to="/design"
              data-testid="materials-cta"
              className="inline-flex items-center gap-2 bg-nbblack text-white font-head font-bold text-lg px-8 py-4 rounded-full nb-border nb-shadow nb-press"
            >
              {t("landing.designCta")} <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {content && (
        <FaqSection
          items={content.faq_landing}
          title={t("landing.faqTitle")}
          subtitle={t("landing.faqSub")}
          jsonLdId="landing-faq-jsonld"
          testid="landing-faq"
        />
      )}

      <footer className="bg-nbblack text-white/70 py-12 border-t-[3px] border-nbpink">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <img src={LOGO_URL} alt="2T Sticker" className="h-12 w-auto brightness-0 invert opacity-90 mb-3" />
            <p className="font-head font-bold text-white text-lg">2T Sticker <span className="text-white/50 font-body font-normal">by 2TProd Studios Inc.</span></p>
          </div>
          <div className="text-sm leading-relaxed not-italic">
            <p className="font-bold text-white">
              <a href="https://2tprodstudios.ca" target="_blank" rel="noreferrer" data-testid="footer-company-link" className="hover:text-nbpink transition-colors">
                2TProd Studios Inc.
              </a>
            </p>
            <p>210b 6e Avenue</p>
            <p>L'Île-Perrot, QC J7V 4V4</p>
            <p>Canada</p>
            <p className="mt-1">
              <a href="tel:+15145363097" data-testid="support-phone-footer" className="hover:text-nbpink transition-colors font-semibold text-white">
                {t("common.support")}: 514.536.3097
              </a>
            </p>
            <p>
              <a href="mailto:cs@2tsticker.ca" data-testid="support-email-footer" className="hover:text-nbpink transition-colors font-semibold text-white">
                cs@2tsticker.ca
              </a>
            </p>
            <div className="flex items-center gap-3 mt-4">
              <a href="https://www.facebook.com/2tsticker" target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="social-facebook" className="w-9 h-9 rounded-full bg-white/10 hover:bg-nbpink flex items-center justify-center transition-colors">
                <Facebook size={18} className="text-white" />
              </a>
              <a href="https://www.instagram.com/2tsticker" target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="social-instagram" className="w-9 h-9 rounded-full bg-white/10 hover:bg-nbpink flex items-center justify-center transition-colors">
                <Instagram size={18} className="text-white" />
              </a>
              <a href="https://www.youtube.com/@2tsticker" target="_blank" rel="noreferrer" aria-label="YouTube" data-testid="social-youtube" className="w-9 h-9 rounded-full bg-white/10 hover:bg-nbpink flex items-center justify-center transition-colors">
                <Youtube size={18} className="text-white" />
              </a>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-8 pt-6 border-t border-white/10 text-sm">
          © {new Date().getFullYear()} <a href="https://2tprodstudios.ca" target="_blank" rel="noreferrer" className="hover:text-nbpink transition-colors">2TProd Studios Inc.</a> {t("landing.rights")}
        </div>
      </footer>
    </div>
  );
}
